import {
  baseApi,
  type AuthProviders,
  type AuthResponse,
  type ChangePasswordRequest,
  type LoginRequest,
  type RegisterRequest,
  type SessionDto,
} from '@/shared/api';
import {
  sessionStarted,
  storedPersistence,
  toSession,
  type SessionPersistence,
} from '@/shared/session';

export interface LoginArgs {
  credentials: LoginRequest;
  /** Keep the session after the browser is closed. */
  rememberMe: boolean;
}

export interface RegisterArgs {
  account: RegisterRequest;
  rememberMe: boolean;
}

const persistenceFor = (rememberMe: boolean): SessionPersistence =>
  rememberMe ? 'local' : 'session';

export const authApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getAuthProviders: build.query<AuthProviders, undefined>({
      query: () => '/api/auth/providers',
    }),
    /** The signed-in user's browsers, most recently used first. */
    getSessions: build.query<SessionDto[], undefined>({
      query: () => '/api/auth/sessions',
      providesTags: ['Session'],
    }),
    /** Signs out one browser; its tokens stop working within 30 seconds. */
    revokeSession: build.mutation<undefined, string>({
      query: (id) => ({ url: `/api/auth/sessions/${encodeURIComponent(id)}`, method: 'DELETE' }),
      invalidatesTags: ['Session'],
    }),
    /** Signs out every browser except this one. */
    signOutOtherSessions: build.mutation<{ signedOut: number }, undefined>({
      query: () => ({ url: '/api/auth/sessions/sign-out-others', method: 'POST' }),
      invalidatesTags: ['Session'],
    }),
    /** Exchanges a Microsoft ID token for a portal session (existing accounts only; always kept signed in). */
    microsoftSignIn: build.mutation<AuthResponse, { idToken: string }>({
      query: (body) => ({ url: '/api/auth/microsoft', method: 'POST', body }),
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(sessionStarted({ session: toSession(data), persistence: 'local' }));
        } catch {
          // Failure is surfaced to the caller through the mutation result.
        }
      },
    }),
    /**
     * Changes the signed-in user's password (400 with field errors otherwise). The API returns a new
     * token, because tokens issued before the change stop working, so it replaces the session.
     */
    changePassword: build.mutation<AuthResponse, ChangePasswordRequest>({
      query: (body) => ({ url: '/api/auth/password', method: 'POST', body }),
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          // Keep the session where it is: the API keeps this browser's "keep me signed in" choice too.
          dispatch(sessionStarted({ session: toSession(data), persistence: storedPersistence() }));
        } catch {
          // Failure is surfaced to the caller through the mutation result.
        }
      },
    }),
    login: build.mutation<AuthResponse, LoginArgs>({
      query: ({ credentials, rememberMe }) => ({
        url: '/api/auth/login',
        method: 'POST',
        body: { ...credentials, rememberMe },
      }),
      async onQueryStarted({ rememberMe }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            sessionStarted({ session: toSession(data), persistence: persistenceFor(rememberMe) }),
          );
        } catch {
          // Failure is surfaced to the caller through the mutation result.
        }
      },
    }),
    register: build.mutation<AuthResponse, RegisterArgs>({
      query: ({ account, rememberMe }) => ({
        url: '/api/auth/register',
        method: 'POST',
        body: { ...account, rememberMe },
      }),
      async onQueryStarted({ rememberMe }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            sessionStarted({ session: toSession(data), persistence: persistenceFor(rememberMe) }),
          );
        } catch {
          // Failure is surfaced to the caller through the mutation result.
        }
      },
    }),
  }),
});

export const {
  useChangePasswordMutation,
  useGetAuthProvidersQuery,
  useLoginMutation,
  useMicrosoftSignInMutation,
  useGetSessionsQuery,
  useRevokeSessionMutation,
  useSignOutOtherSessionsMutation,
  useRegisterMutation,
} = authApi;
